/**
 * Opens a print dialog with formatted document content for PDF generation.
 * Uses the browser's built-in print-to-PDF capability for maximum compatibility.
 */
export function downloadDocumentAsPdf(content: string, title: string) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const escapedContent = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</title>
      <style>
        body {
          font-family: 'Georgia', serif;
          max-width: 700px;
          margin: 40px auto;
          padding: 20px;
          line-height: 1.6;
          color: #1a1a1a;
        }
        @media print {
          body {
            margin: 0;
            padding: 20px;
          }
        }
      </style>
    </head>
    <body>
      <pre style="white-space: pre-wrap; font-family: inherit;">${escapedContent}</pre>
    </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.print();
}

/** Convert plain text to HTML for rich clipboard paste */
export function textToHtml(text: string): string {
  return text.split('\n\n').map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
}

/** Download content as .txt file */
export function downloadAsTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.txt') ? filename : `${filename}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Copy as rich HTML to clipboard (for pasting into email clients with formatting) */
export async function copyAsHtml(text: string): Promise<boolean> {
  const html = textToHtml(text);
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
    return true;
  } catch {
    try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
  }
}
